import { useTranslation } from "react-i18next";
import { Type, Play, Square, Pipette } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { VideoFormat } from "@/lib/hooks/useImageUpload";
import type { WatermarkSettings } from "../video-form-utils";

interface TitleControlsProps {
  format: VideoFormat;
  watermarkSettingsByFormat: Record<VideoFormat, WatermarkSettings>;
  setWatermarkSettingsByFormat: React.Dispatch<
    React.SetStateAction<Record<VideoFormat, WatermarkSettings>>
  >;
  isPlaying: boolean;
  introStylesList: readonly any[];
  presetColors: any[];
  getContrastColor: (hexColor: string) => string;
  setStopTrigger: React.Dispatch<React.SetStateAction<number>>;
  setPreviewTrigger: React.Dispatch<React.SetStateAction<number>>;
  getContainerAlignment: (alignment: string) => React.CSSProperties;
  googleFonts: any[];
}

export const TitleControls = ({
  format,
  watermarkSettingsByFormat,
  setWatermarkSettingsByFormat,
  isPlaying,
  introStylesList,
  presetColors,
  getContrastColor,
  setStopTrigger,
  setPreviewTrigger,
  getContainerAlignment,
  googleFonts,
}: TitleControlsProps) => {
  const { t } = useTranslation();

  const isIntroEnabled = watermarkSettingsByFormat[format].introEnabled ?? true;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Toggle Active */}
      <div className="flex items-center justify-between p-4 bg-black/5 rounded-2xl border border-black/5 transition-all">
        <div className="space-y-0.5">
          <Label className="text-sm font-bold">{t("createVideo.introActive") || "Show Opening Title"}</Label>
          <p className="text-[10px] text-muted-foreground">{t("createVideo.introActiveHint") || "Toggle visibility of your opening title clip"}</p>
        </div>
        <Checkbox 
          checked={isIntroEnabled}
          onCheckedChange={(checked) => {
            setWatermarkSettingsByFormat((prev) => ({
              ...prev,
              [format]: { ...prev[format], introEnabled: !!checked },
            }));
          }}
          className="size-6 rounded-lg data-[state=checked]:bg-black data-[state=checked]:border-black"
        />
      </div>

      <div className={cn("space-y-8 transition-all duration-300", !isIntroEnabled && "opacity-40 grayscale pointer-events-none")}>
        <div className="space-y-8">
          {/* 1. Title Style Preset */}
          <div className="space-y-4">
            <Label className="text-sm font-bold">
              {t("createVideo.introStyles.title") || "Title Style"}
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {introStylesList.map((style) => (
                <button
                  key={style.id}
                  type="button"
                  disabled={isPlaying || !isIntroEnabled}
                  onClick={() =>
                    setWatermarkSettingsByFormat((prev) => ({
                      ...prev,
                      [format]: {
                        ...prev[format],
                        introStyle: style.id,
                      },
                    }))
                  }
                  className={cn(
                    "px-3 py-2 text-[10px] sm:text-xs font-bold rounded-xl border border-black/5 transition-all text-center capitalize",
                    watermarkSettingsByFormat[format].introStyle === style.id
                      ? "bg-black text-white shadow-lg scale-[1.02]"
                      : "bg-white text-muted-foreground hover:bg-black/5",
                    (isPlaying || !isIntroEnabled) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {style.label}
                </button>
              ))}
            </div>
          </div>

          {/* NOTE: Animation Style and Preview commented out. Will be reimplemented later. */}
          {/* 
          <div className="space-y-4">
            <Label className="text-sm font-bold">
              {t("createVideo.animations.title") || "Animation Style"}
            </Label>
            <div className="flex flex-col gap-2">
              <div className="flex-1">
                <Select
                  disabled={isPlaying || !isIntroEnabled}
                  value={watermarkSettingsByFormat[format].introAnimation || "fade"}
                  onValueChange={(v) =>
                    setWatermarkSettingsByFormat((prev) => ({
                      ...prev,
                      [format]: {
                        ...prev[format],
                        introAnimation: v,
                      },
                    }))
                  }
                >
                  <SelectTrigger className="w-full bg-white border-black/5 rounded-xl py-6 shadow-sm hover:bg-black/2 transition-all">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-md border-black/10 rounded-xl shadow-2xl">
                    <SelectItem value="fade">{t("createVideo.animations.fade") || "Fade"}</SelectItem>
                    <SelectItem value="slide">{t("createVideo.animations.slide") || "Slide"}</SelectItem>
                    <SelectItem value="zoom">{t("createVideo.animations.zoom") || "Zoom"}</SelectItem>
                    <SelectItem value="reveal">{t("createVideo.animations.reveal") || "Reveal"}</SelectItem>
                    <SelectItem value="revealUp">{t("createVideo.animations.revealUp") || "Reveal Up"}</SelectItem>
                    <SelectItem value="bounce">{t("createVideo.animations.bounce") || "Bounce"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                disabled={!isIntroEnabled}
                onClick={() => isPlaying ? setStopTrigger(Date.now()) : setPreviewTrigger(Date.now())}
                className={cn(
                  "w-full py-4 rounded-xl transition-all font-bold shadow-lg flex items-center justify-center gap-2",
                  isPlaying 
                    ? "bg-red-500 text-white hover:bg-red-600" 
                    : "bg-black text-white hover:bg-black/80",
                  !isIntroEnabled && "opacity-50 cursor-not-allowed pointer-events-none"
                )}
                title={isPlaying ? (t("common.stop") || "Stop") : (t("createVideo.previewAnimation") || "Preview Animation")}
              >
                {isPlaying ? (
                  <>
                    <Square className="w-4 h-4 fill-current" />
                    <span>{t("common.stop") || "Stop Preview"}</span>
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 fill-current" />
                    <span>{t("createVideo.previewIntro") || "Preview Intro"}</span>
                  </>
                )}
              </button>
            </div>
          </div>
          */}

          {/* 3. Alignment */}
          <div className="space-y-4">
            <Label className="text-sm font-bold">
              {t("createVideo.alignment") || "Alignment"}
            </Label>
            <RadioGroup
              disabled={isPlaying || !isIntroEnabled}
              value={watermarkSettingsByFormat[format].introAlignment || "center"}
              onValueChange={(v) =>
                setWatermarkSettingsByFormat((prev) => ({
                  ...prev,
                  [format]: {
                    ...prev[format],
                    introAlignment: v,
                  },
                }))
              }
              className="grid grid-cols-3 gap-2"
            >
              {[
                "top-left", "top-center", "top-right",
                "center-left", "center", "center-right",
                "bottom-left", "bottom-center", "bottom-right"
              ].map((pos) => (
                <Label
                  key={pos}
                  htmlFor={`intro-${format}-${pos}`}
                  className={cn(
                    "flex items-center justify-center rounded-xl border border-black/5 bg-white hover:bg-black hover:text-white h-12 2k:h-20 4k:h-32 cursor-pointer transition-all",
                    watermarkSettingsByFormat[format].introAlignment === pos &&
                      "bg-black text-white shadow-lg",
                  )}
                >
                  <RadioGroupItem
                    value={pos}
                    id={`intro-${format}-${pos}`}
                    className="sr-only"
                  />
                  <div className="w-full h-full flex p-1.5" style={getContainerAlignment(pos)}>
                    <div 
                      className={cn(
                        "w-2 h-2 rounded-full",
                        watermarkSettingsByFormat[format].introAlignment === pos
                          ? "bg-white"
                          : "bg-black"
                      )}
                    />
                  </div>
                </Label>
              ))}
            </RadioGroup>
          </div>

          {/* 4. Font Selection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-bold flex items-center gap-2">
                <Type size={14} className="opacity-40" />
                {t("createVideo.introStyles.font") || "Font Family"}
              </Label>
              {watermarkSettingsByFormat[format].introFont && (
                <button 
                  disabled={isPlaying || !isIntroEnabled}
                  onClick={() => setWatermarkSettingsByFormat(prev => ({
                    ...prev,
                    [format]: { ...prev[format], introFont: "" }
                  }))}
                  className={cn(
                    "text-[10px] text-muted-foreground hover:text-black transition-colors",
                    (isPlaying || !isIntroEnabled) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {t("createVideo.introStyles.resetToDefault") || "Reset to Default"}
                </button>
              )}
            </div>

            <Select
              disabled={isPlaying || !isIntroEnabled}
              value={watermarkSettingsByFormat[format].introFont || "default"}
              onValueChange={(val) => 
                setWatermarkSettingsByFormat((prev) => ({
                  ...prev,
                  [format]: {
                    ...prev[format],
                    introFont: val === "default" ? "" : val,
                  },
                }))
              }
            >
              <SelectTrigger className="w-full bg-white border-black/5 rounded-xl py-6 shadow-sm hover:bg-black/2 transition-all text-left flex items-center justify-between">
                <SelectValue placeholder="Select a font" />
              </SelectTrigger>
              <SelectContent className="bg-white/95 backdrop-blur-md border-black/10 rounded-xl shadow-2xl">
                <SelectItem value="default" className="text-xs font-medium py-2">
                  Default Style Font
                </SelectItem>
                <div className="h-px bg-black/5 my-1 mx-2" />
                {googleFonts.map((font) => (
                  <SelectItem 
                    key={font.id} 
                    value={font.id}
                    className={cn(
                      "text-sm py-2",
                      `font-${font.id}`
                    )}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-bold">{font.name}</span>
                      <span className="opacity-50 text-[9px] uppercase tracking-wider font-sans">{font.category}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 5. Background Color */}
          <div className="space-y-4">
            <Label className="text-sm font-bold">
              {t("createVideo.introStyles.background") || "Background Color"}
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {presetColors.map((color) => (
                <button
                  disabled={isPlaying || !isIntroEnabled}
                  key={color.value}
                  type="button"
                  onClick={() => {
                    const textColor = getContrastColor(color.value);
                    setWatermarkSettingsByFormat((prev) => ({
                      ...prev,
                      [format]: {
                        ...prev[format],
                        introBgColor: color.value,
                        introTextColor: textColor,
                      },
                    }));
                  }}
                  className={cn(
                    "w-8 h-8 rounded-full border border-black/10 transition-all",
                    watermarkSettingsByFormat[format].introBgColor === color.value
                      ? "ring-2 ring-black ring-offset-2 scale-110"
                      : "hover:scale-105",
                    (isPlaying || !isIntroEnabled) && "opacity-50 cursor-not-allowed"
                  )}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}

              <label
                className={cn(
                  "w-8 h-8 rounded-full border border-black/10 cursor-pointer transition-all flex items-center justify-center relative",
                  !presetColors.some(c => c.value === watermarkSettingsByFormat[format].introBgColor)
                    ? "ring-2 ring-black ring-offset-2 scale-110"
                    : "hover:scale-105",
                  (isPlaying || !isIntroEnabled) && "opacity-50 cursor-not-allowed pointer-events-none"
                )}
                style={{ backgroundColor: watermarkSettingsByFormat[format].introBgColor || "#000000" }}
                title="Custom Color"
              >
                <input
                  disabled={isPlaying || !isIntroEnabled}
                  type="color"
                  value={watermarkSettingsByFormat[format].introBgColor || "#000000"}
                  onChange={(e) => {
                    const color = e.target.value;
                    const textColor = getContrastColor(color);
                    setWatermarkSettingsByFormat((prev) => ({
                      ...prev,
                      [format]: {
                        ...prev[format],
                        introBgColor: color,
                        introTextColor: textColor,
                      },
                    }));
                  }}
                  className="sr-only"
                />
                <Pipette 
                  className="w-3.5 h-3.5" 
                  style={{ color: getContrastColor(watermarkSettingsByFormat[format].introBgColor || "#000000") }}
                />
              </label>
            </div>
          </div>

          {/* 6. Background Opacity */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-bold">
                {t("createVideo.introStyles.bgOpacity") || "Background Opacity"}
              </Label>
              <span className="text-xs font-mono bg-black/5 px-2 py-0.5 rounded">
                {watermarkSettingsByFormat[format].introBgOpacity ?? 75}%
              </span>
            </div>
            <Slider
              disabled={isPlaying || !isIntroEnabled}
              min={0}
              max={100}
              step={1}
              value={[watermarkSettingsByFormat[format].introBgOpacity ?? 75]}
              onValueChange={(v) =>
                setWatermarkSettingsByFormat((prev) => ({
                  ...prev,
                  [format]: {
                    ...prev[format],
                    introBgOpacity: v[0],
                  },
                }))
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
